/** @file collectEvents: the clip events on every frame passed between two times, twins in the mirrored half. */
import type { Clip, ClipKey } from '@zilo/pose3d-format/types/clip';
import type { FiredEvent } from '@zilo/pose3d-pose/events/eventTypes';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import { buildChannels } from '@zilo/pose3d-pose/sample/buildChannels';

const EPS = 1e-6;

/** Clip frame of an unwrapped frame (decision 4), or -1 past a one-shot's last frame. */
function frameOfRaw(clip: Clip, raw: number): number {
  const len = clip.frames - clip.loopStart;
  if (clip.loop && len > 0) return raw < clip.frames ? raw : clip.loopStart + ((raw - clip.loopStart) % len);
  return raw > clip.frames ? -1 : raw;
}

/**
 * Appends to `out`, in raw order, the events of every unwrapped frame in `(raw(from), raw(to)]` (`from === null` ->
 * `[start, raw(to)]`), `raw(t) = start + floor(t * fps)`. Each raw frame maps to a clip frame by the loop rule; in the
 * mirrored half (`f >= loopStart + half`) the events of `f - half` fire with `node` swapped to its twin and `pos.x` negated.
 */
export function collectEvents(clip: Clip, rig: Rig, fromTimeSec: number | null, toTimeSec: number, out: FiredEvent[]): FiredEvent[] {
  const rawOf = (t: number) => clip.start + Math.floor(Math.max(0, t) * clip.fps + EPS);
  const lo = fromTimeSec === null ? clip.start - 1 : rawOf(fromTimeSec);
  const hi = rawOf(toTimeSec);
  const { half } = buildChannels(clip, rig);
  for (let raw = lo + 1; raw <= hi; raw++) {
    const frame = frameOfRaw(clip, raw);
    if (frame < 0) break;
    const mirrored = half > 0 && frame >= clip.loopStart + half;
    const src = mirrored ? frame - half : frame;
    const key: ClipKey | undefined = clip.keys.find((k) => k.frame === src);
    for (const event of key?.events ?? []) {
      const i = event.node === undefined ? undefined : rig.index.get(event.node);
      const twin = i === undefined ? undefined : rig.names[rig.twin[i] ?? i];
      const pos = event.pos;
      out.push({ frame, raw, event: !mirrored ? event : {
        ...event,
        ...(twin === undefined ? {} : { node: twin }),
        ...(pos === undefined ? {} : { pos: [-pos[0], pos[1], pos[2]] }),
      } });
    }
  }
  return out;
}
